import {
  VOUCHER_STATUS_NAME_LABELS,
  VoucherStatusNameSchema,
  type Locale,
  type VoucherStatusName,
} from '@voll-intelligence/domain';

import { FLIGHT_VOUCHER_CATALOG } from './flight_voucher_catalog.js';
import { BUS_VOUCHER_CATALOG } from './bus_voucher_catalog.js';
import { HOTEL_VOUCHER_CATALOG } from './hotel_voucher_catalog.js';
import { PAYMENT_VOUCHER_CATALOG } from './payment_voucher_catalog.js';
import { RENTAL_VOUCHER_CATALOG } from './rental_voucher_catalog.js';
import { MOBILITY_VOUCHER_CATALOG } from './mobility_voucher_catalog.js';
import { EXPENSE_VOUCHER_CATALOG } from './expense_voucher_catalog.js';
import { SCHEDULED_RIDE_VOUCHER_CATALOG } from './scheduled_ride_voucher_catalog.js';
import { REQUEST_SCREENS_CATALOG } from './request_screens_catalog.js';

/** `trips.voucherStatus.<NOME>` → o nome da VOLL antiga, da tabela única do domain. A
 *  chave segue literal no tipo: o `t` tipado do web lê estas chaves. */
type VoucherStatusEntries = { [N in VoucherStatusName as `trips.voucherStatus.${N}`]: string };

function voucherStatusEntries(locale: Locale): VoucherStatusEntries {
  return Object.fromEntries(
    VoucherStatusNameSchema.options.map((name) => [
      `trips.voucherStatus.${name}`,
      VOUCHER_STATUS_NAME_LABELS[locale][name],
    ]),
  ) as VoucherStatusEntries;
}

const PT_BR = {
  'trips.services.seat': 'Assento',
  'trips.services.class': 'Classe',
  'trips.services.cabin': 'Cabine',
  'trips.services.baggage': 'Bagagem',
  'trips.services.baggageKg': 'Até {{kg}} kg',
  'trips.services.baggageIncluded': 'Despachada incluída',
  'trips.services.baggageNone': 'Sem bagagem despachada',
  // A franquia DECLARADA pela tarifa (peças/peso). Vence as três acima porque é
  // a única que diz quanto: as outras são o número cru (que mistura mão com
  // despacho) e o booleano. Ver `baggageLabel`.
  'trips.services.baggagePieces_one': '{{count}} peça despachada',
  'trips.services.baggagePieces_other': '{{count}} peças despachadas',
  'trips.services.baggagePiecesKg_one': '{{count}} peça despachada de {{kg}} kg',
  'trips.services.baggagePiecesKg_other': '{{count}} peças despachadas de {{kg}} kg',
  'trips.services.baggageCheckedKg': 'Despachada até {{kg}} kg',
  'trips.services.carryBaggage': 'Bagagem de mão',
  'trips.services.baggageCarryIncluded': 'Incluída',
  'trips.services.baggageCarryNone': 'Não incluída',
  'trips.services.baggageCarryPieces_one': '{{count}} peça',
  'trips.services.baggageCarryPieces_other': '{{count}} peças',
  'trips.services.baggageCarryPiecesKg_one': '{{count}} peça de {{kg}} kg',
  'trips.services.baggageCarryPiecesKg_other': '{{count}} peças de {{kg}} kg',
  'trips.services.locator': 'Localizador',
  'trips.services.internalLocator': 'Código interno',
  'trips.services.from': 'Origem',
  'trips.services.to': 'Destino',
  'trips.services.guests': 'Hóspedes',
  'trips.services.rooms': 'Quartos',
  'trips.services.reservation': 'Reserva',
  'trips.services.legOut': 'Ida',
  'trips.services.legBack': 'Volta',
  'trips.services.empty': 'Esta viagem ainda não tem serviços emitidos.',
  'trips.services.error': 'Não foi possível carregar os serviços da viagem. Tente novamente.',
  'trips.services.noDate': 's/ data',
  'trips.services.pickup': 'Retirada',
  'trips.services.dropoff': 'Devolução',
  'trips.services.distanceValue': '{{value}} km',
  'trips.voucher.modal.FLIGHT': 'Aéreo',
  'trips.voucher.modal.HOTEL': 'Hospedagem',
  'trips.voucher.modal.BUS': 'Ônibus',
  'trips.voucher.modal.RENTAL': 'Locação',
  'trips.voucher.modal.MOBILITY': 'Transfer',
  'trips.voucher.copyLocator': 'o localizador deste bilhete',
  'trips.voucher.copyReservation': 'o número da reserva',
  'trips.voucher.code': 'Voucher',
  'trips.voucher.copyCode': 'o código do voucher',
  'trips.voucher.copyAddress': 'o endereço',
  'trips.voucher.arrivesOn': 'chega {{day}}',
  'trips.voucher.layover': 'Conexão',
  'trips.voucher.layoverAt': 'Conexão em {{place}}',
  'trips.voucher.layoverWait': '{{wait}} de espera',
  'trips.voucher.layoverChangeAirport': 'troca de aeroporto',
  'trips.voucher.singleLeg': 'Trecho único',
  'trips.voucher.legN': 'Trecho {{n}}',
  'trips.voucher.separateTickets': 'Bilhetes separados',
  'trips.voucher.separateTicketsNote':
    'O próximo trecho está em outro bilhete, com outro localizador. Se este atrasar, a companhia não precisa reacomodar você.',
  'trips.voucher.localTimes': 'Horários locais de cada aeroporto.',
  'trips.voucher.localTimesWith': 'Horários locais de cada aeroporto. {{zones}}.',
  'trips.voucher.roundTrip': 'Ida e volta',
  'trips.voucher.oneWay': 'Só ida',
  'trips.voucher.multiLeg': 'Multitrechos',
  'trips.voucher.nights_one': '{{count}} noite',
  'trips.voucher.nights_other': '{{count}} noites',
  'trips.voucher.checkIn': 'Entrada',
  'trips.voucher.checkOut': 'Saída',
  'trips.voucher.fromHour': 'a partir de {{hour}}',
  'trips.voucher.untilHour': 'até {{hour}}',
  'trips.voucher.stayRules': 'Regras da estadia',
  'trips.voucher.cancellation': 'Cancelamento',
  'trips.voucher.freeUntil': 'sem custo até {{day}}',
  'trips.voucher.afterFreeCancellation':
    'Depois dessa data, a primeira diária é cobrada. A gente confirma o valor antes.',
  'trips.voucher.penaltyAfter': 'Multa depois dessa data',
  'trips.voucher.category': 'Categoria',
  'trips.voucher.distance': 'Distância',
  'trips.voucher.duration': 'Duração',
  'trips.voucher.requester': 'Solicitante',
  'trips.voucher.requestData': 'Dados da solicitação',
  'trips.voucher.awaitingApprovers': 'Aguardando {{names}}',
  'trips.voucher.motive': 'Motivo da viagem',
  'trips.voucher.costCenter': 'Centro de custo',
  'trips.voucher.company': 'Empresa',
  'trips.voucher.project': 'Projeto',
  'trips.voucher.obs': 'Observação',
  'trips.voucher.costCenterSplit': '{{name}} · {{percent}}%',
  'trips.voucher.inPolicy': 'Dentro da política',
  'trips.voucher.outPolicy': 'Fora da política',
  'trips.voucher.issuedBy': 'Emitido pela VOLL',
  'trips.voucher.accommodation': 'Acomodação',
  'trips.agenda.flightOut': 'Voo de ida',
  'trips.agenda.flightBack': 'Voo de volta',
  'trips.agenda.checkIn': 'check-in',
  'trips.agenda.checkOut': 'check-out',
  'trips.agenda.nights_one': '{{count}} noite',
  'trips.agenda.nights_other': '{{count}} noites',
  'trips.featured.direct': 'direto',
  'trips.featured.stops_one': '{{count}} parada',
  'trips.featured.stops_other': '{{count}} paradas',
  'trips.calendar.modal.flight': 'Voos',
  'trips.calendar.modal.hotel': 'Hotel',
  'trips.calendar.modal.bus': 'Ônibus',
  'trips.calendar.modal.rental': 'Carro',
  'trips.calendar.modal.mobility': 'Transfer',
  // Vocabulário da HOME ("Aéreo", "Carros") — o de `serviceHeading`/`stopTitle`
  // quando a fonte não deu nem fornecedor nem nome próprio. Terceira família de
  // cópia de modal, distinta da de mercado (`trips.calendar.modal.*`) e da do
  // documento (`trips.voucher.modal.*`).
  'home.service.flight.label': 'Aéreo',
  'home.service.hotel.label': 'Hotel',
  'home.service.bus.label': 'Ônibus',
  'home.service.rental.label': 'Carros',
  'home.service.mobility.label': 'Mobilidade',
  ...voucherStatusEntries('pt-BR'),
  'trips.cabin.economy': 'Econômica',
  'trips.cabin.premium': 'Premium',
  'trips.cabin.business': 'Executiva',
  'trips.cabin.first': 'Primeira',
  'trips.panel.itinerary': 'Itinerário',
  'trips.panel.total': 'Total da viagem',
  'doc.title': 'Documento de viagem',
  'doc.eyebrow': 'Viagem',
  'doc.generatedAt': 'Gerado em {{ts}}',
  'doc.values': 'Valores',
  'doc.disclaimer':
    'Horários no fuso local de cada cidade. Portão, terminal e mudanças de última hora: confira no app — este documento retrata a viagem em {{ts}}.',
  'doc.readinessIssued': 'Emitida',
  'doc.readinessPending': 'Em emissão',
  'doc.tripFallback': 'Sua viagem',
  'doc.total': 'Total',
  'doc.moreInfo': 'Mais informações',
} satisfies Record<string, string>;

const EN = {
  'trips.services.seat': 'Seat',
  'trips.services.class': 'Class',
  'trips.services.cabin': 'Cabin',
  'trips.services.baggage': 'Baggage',
  'trips.services.baggageKg': 'Up to {{kg}} kg',
  'trips.services.baggageIncluded': 'Checked bag included',
  'trips.services.baggageNone': 'No checked bag',
  'trips.services.baggagePieces_one': '{{count}} checked bag',
  'trips.services.baggagePieces_other': '{{count}} checked bags',
  'trips.services.baggagePiecesKg_one': '{{count}} checked bag of {{kg}} kg',
  'trips.services.baggagePiecesKg_other': '{{count}} checked bags of {{kg}} kg',
  'trips.services.baggageCheckedKg': 'Checked bag up to {{kg}} kg',
  'trips.services.carryBaggage': 'Hand baggage',
  'trips.services.baggageCarryIncluded': 'Included',
  'trips.services.baggageCarryNone': 'Not included',
  'trips.services.baggageCarryPieces_one': '{{count}} bag',
  'trips.services.baggageCarryPieces_other': '{{count}} bags',
  'trips.services.baggageCarryPiecesKg_one': '{{count}} bag of {{kg}} kg',
  'trips.services.baggageCarryPiecesKg_other': '{{count}} bags of {{kg}} kg',
  'trips.services.locator': 'Locator',
  'trips.services.internalLocator': 'Internal code',
  'trips.services.from': 'From',
  'trips.services.to': 'To',
  'trips.services.guests': 'Guests',
  'trips.services.rooms': 'Rooms',
  'trips.services.reservation': 'Booking',
  'trips.services.legOut': 'Outbound',
  'trips.services.legBack': 'Return',
  'trips.services.empty': 'This trip has no issued services yet.',
  'trips.services.error': 'We could not load this trip’s services. Try again.',
  'trips.services.noDate': 'no date',
  'trips.services.pickup': 'Pick-up',
  'trips.services.dropoff': 'Drop-off',
  'trips.services.distanceValue': '{{value}} km',
  'trips.voucher.modal.FLIGHT': 'Flight',
  'trips.voucher.modal.HOTEL': 'Stay',
  'trips.voucher.modal.BUS': 'Bus',
  'trips.voucher.modal.RENTAL': 'Car rental',
  'trips.voucher.modal.MOBILITY': 'Transfer',
  'trips.voucher.copyLocator': 'this ticket’s locator',
  'trips.voucher.copyReservation': 'the reservation number',
  'trips.voucher.code': 'Voucher',
  'trips.voucher.copyCode': 'the voucher code',
  'trips.voucher.copyAddress': 'the address',
  'trips.voucher.arrivesOn': 'arrives {{day}}',
  'trips.voucher.layover': 'Layover',
  'trips.voucher.layoverAt': 'Layover in {{place}}',
  'trips.voucher.layoverWait': '{{wait}} wait',
  'trips.voucher.layoverChangeAirport': 'airport change',
  'trips.voucher.singleLeg': 'Single leg',
  'trips.voucher.legN': 'Leg {{n}}',
  'trips.voucher.separateTickets': 'Separate tickets',
  'trips.voucher.separateTicketsNote':
    'The next leg is on another ticket, with a different locator. If this one is delayed, the airline does not have to rebook you.',
  'trips.voucher.localTimes': 'Local time at each airport.',
  'trips.voucher.localTimesWith': 'Local time at each airport. {{zones}}.',
  'trips.voucher.roundTrip': 'Round trip',
  'trips.voucher.oneWay': 'One way',
  'trips.voucher.multiLeg': 'Multi-city',
  'trips.voucher.nights_one': '{{count}} night',
  'trips.voucher.nights_other': '{{count}} nights',
  'trips.voucher.checkIn': 'Check-in',
  'trips.voucher.checkOut': 'Check-out',
  'trips.voucher.fromHour': 'from {{hour}}',
  'trips.voucher.untilHour': 'until {{hour}}',
  'trips.voucher.stayRules': 'Stay rules',
  'trips.voucher.cancellation': 'Cancellation',
  'trips.voucher.freeUntil': 'free until {{day}}',
  'trips.voucher.afterFreeCancellation':
    'After that date the first night is charged. We confirm the amount before charging.',
  'trips.voucher.penaltyAfter': 'Penalty after that date',
  'trips.voucher.category': 'Category',
  'trips.voucher.distance': 'Distance',
  'trips.voucher.duration': 'Duration',
  'trips.voucher.requester': 'Requested by',
  'trips.voucher.requestData': 'Request details',
  'trips.voucher.awaitingApprovers': 'Waiting on {{names}}',
  'trips.voucher.motive': 'Trip purpose',
  'trips.voucher.costCenter': 'Cost center',
  'trips.voucher.company': 'Company',
  'trips.voucher.project': 'Project',
  'trips.voucher.obs': 'Note',
  'trips.voucher.costCenterSplit': '{{name}} · {{percent}}%',
  'trips.voucher.inPolicy': 'Within policy',
  'trips.voucher.outPolicy': 'Out of policy',
  'trips.voucher.issuedBy': 'Issued by VOLL',
  'trips.voucher.accommodation': 'Accommodation',
  'trips.agenda.flightOut': 'Outbound flight',
  'trips.agenda.flightBack': 'Return flight',
  'trips.agenda.checkIn': 'check-in',
  'trips.agenda.checkOut': 'check-out',
  'trips.agenda.nights_one': '{{count}} night',
  'trips.agenda.nights_other': '{{count}} nights',
  'trips.featured.direct': 'nonstop',
  'trips.featured.stops_one': '{{count}} stop',
  'trips.featured.stops_other': '{{count}} stops',
  'trips.calendar.modal.flight': 'Flights',
  'trips.calendar.modal.hotel': 'Hotel',
  'trips.calendar.modal.bus': 'Bus',
  'trips.calendar.modal.rental': 'Car',
  'trips.calendar.modal.mobility': 'Transfer',
  'home.service.flight.label': 'Flights',
  'home.service.hotel.label': 'Hotel',
  'home.service.bus.label': 'Bus',
  'home.service.rental.label': 'Cars',
  'home.service.mobility.label': 'Mobility',
  ...voucherStatusEntries('en'),
  'trips.cabin.economy': 'Economy',
  'trips.cabin.premium': 'Premium',
  'trips.cabin.business': 'Business',
  'trips.cabin.first': 'First',
  'trips.panel.itinerary': 'Itinerary',
  'trips.panel.total': 'Trip total',
  'doc.title': 'Trip document',
  'doc.eyebrow': 'Trip',
  'doc.generatedAt': 'Generated {{ts}}',
  'doc.values': 'Amounts',
  'doc.disclaimer':
    'Times are local to each city. Gate, terminal and last-minute changes: check the app — this document reflects the trip as of {{ts}}.',
  'doc.readinessIssued': 'Issued',
  'doc.readinessPending': 'Being issued',
  'doc.tripFallback': 'Your trip',
  'doc.total': 'Total',
  'doc.moreInfo': 'More information',
} satisfies Record<string, string>;

const ES = {
  'trips.services.seat': 'Asiento',
  'trips.services.class': 'Clase',
  'trips.services.cabin': 'Cabina',
  'trips.services.baggage': 'Equipaje',
  'trips.services.baggageKg': 'Hasta {{kg}} kg',
  'trips.services.baggageIncluded': 'Equipaje facturado incluido',
  'trips.services.baggageNone': 'Sin equipaje facturado',
  'trips.services.baggagePieces_one': '{{count}} pieza facturada',
  'trips.services.baggagePieces_other': '{{count}} piezas facturadas',
  'trips.services.baggagePiecesKg_one': '{{count}} pieza facturada de {{kg}} kg',
  'trips.services.baggagePiecesKg_other': '{{count}} piezas facturadas de {{kg}} kg',
  'trips.services.baggageCheckedKg': 'Equipaje facturado hasta {{kg}} kg',
  'trips.services.carryBaggage': 'Equipaje de mano',
  'trips.services.baggageCarryIncluded': 'Incluido',
  'trips.services.baggageCarryNone': 'No incluido',
  'trips.services.baggageCarryPieces_one': '{{count}} pieza',
  'trips.services.baggageCarryPieces_other': '{{count}} piezas',
  'trips.services.baggageCarryPiecesKg_one': '{{count}} pieza de {{kg}} kg',
  'trips.services.baggageCarryPiecesKg_other': '{{count}} piezas de {{kg}} kg',
  'trips.services.locator': 'Localizador',
  'trips.services.internalLocator': 'Código interno',
  'trips.services.from': 'Origen',
  'trips.services.to': 'Destino',
  'trips.services.guests': 'Huéspedes',
  'trips.services.rooms': 'Habitaciones',
  'trips.services.reservation': 'Reserva',
  'trips.services.legOut': 'Ida',
  'trips.services.legBack': 'Vuelta',
  'trips.services.empty': 'Este viaje aún no tiene servicios emitidos.',
  'trips.services.error': 'No se pudieron cargar los servicios del viaje. Inténtalo de nuevo.',
  'trips.services.noDate': 'sin fecha',
  'trips.services.pickup': 'Retiro',
  'trips.services.dropoff': 'Devolución',
  'trips.services.distanceValue': '{{value}} km',
  'trips.voucher.modal.FLIGHT': 'Aéreo',
  'trips.voucher.modal.HOTEL': 'Alojamiento',
  'trips.voucher.modal.BUS': 'Autobús',
  'trips.voucher.modal.RENTAL': 'Alquiler',
  'trips.voucher.modal.MOBILITY': 'Transfer',
  'trips.voucher.copyLocator': 'el localizador de este billete',
  'trips.voucher.copyReservation': 'el número de reserva',
  'trips.voucher.code': 'Voucher',
  'trips.voucher.copyCode': 'el código del voucher',
  'trips.voucher.copyAddress': 'la dirección',
  'trips.voucher.arrivesOn': 'llega {{day}}',
  'trips.voucher.layover': 'Conexión',
  'trips.voucher.layoverAt': 'Conexión en {{place}}',
  'trips.voucher.layoverWait': '{{wait}} de espera',
  'trips.voucher.layoverChangeAirport': 'cambio de aeropuerto',
  'trips.voucher.singleLeg': 'Trayecto único',
  'trips.voucher.legN': 'Trayecto {{n}}',
  'trips.voucher.separateTickets': 'Billetes separados',
  'trips.voucher.separateTicketsNote':
    'El próximo trayecto está en otro billete, con otro localizador. Si este se retrasa, la aerolínea no tiene que reubicarte.',
  'trips.voucher.localTimes': 'Horarios locales de cada aeropuerto.',
  'trips.voucher.localTimesWith': 'Horarios locales de cada aeropuerto. {{zones}}.',
  'trips.voucher.roundTrip': 'Ida y vuelta',
  'trips.voucher.oneWay': 'Solo ida',
  'trips.voucher.multiLeg': 'Multidestino',
  'trips.voucher.nights_one': '{{count}} noche',
  'trips.voucher.nights_other': '{{count}} noches',
  'trips.voucher.checkIn': 'Entrada',
  'trips.voucher.checkOut': 'Salida',
  'trips.voucher.fromHour': 'a partir de {{hour}}',
  'trips.voucher.untilHour': 'hasta {{hour}}',
  'trips.voucher.stayRules': 'Reglas de la estancia',
  'trips.voucher.cancellation': 'Cancelación',
  'trips.voucher.freeUntil': 'sin coste hasta {{day}}',
  'trips.voucher.afterFreeCancellation':
    'Después de esa fecha se cobra la primera noche. Confirmamos el importe antes.',
  'trips.voucher.penaltyAfter': 'Multa después de esa fecha',
  'trips.voucher.category': 'Categoría',
  'trips.voucher.distance': 'Distancia',
  'trips.voucher.duration': 'Duración',
  'trips.voucher.requester': 'Solicitante',
  'trips.voucher.requestData': 'Datos de la solicitud',
  'trips.voucher.awaitingApprovers': 'Esperando a {{names}}',
  'trips.voucher.motive': 'Motivo del viaje',
  'trips.voucher.costCenter': 'Centro de costo',
  'trips.voucher.company': 'Empresa',
  'trips.voucher.project': 'Proyecto',
  'trips.voucher.obs': 'Observación',
  'trips.voucher.costCenterSplit': '{{name}} · {{percent}}%',
  'trips.voucher.inPolicy': 'Dentro de la política',
  'trips.voucher.outPolicy': 'Fuera de la política',
  'trips.voucher.issuedBy': 'Emitido por VOLL',
  'trips.voucher.accommodation': 'Alojamiento',
  'trips.agenda.flightOut': 'Vuelo de ida',
  'trips.agenda.flightBack': 'Vuelo de vuelta',
  'trips.agenda.checkIn': 'check-in',
  'trips.agenda.checkOut': 'check-out',
  'trips.agenda.nights_one': '{{count}} noche',
  'trips.agenda.nights_other': '{{count}} noches',
  'trips.featured.direct': 'directo',
  'trips.featured.stops_one': '{{count}} escala',
  'trips.featured.stops_other': '{{count}} escalas',
  'trips.calendar.modal.flight': 'Vuelos',
  'trips.calendar.modal.hotel': 'Hotel',
  'trips.calendar.modal.bus': 'Autobús',
  'trips.calendar.modal.rental': 'Auto',
  'trips.calendar.modal.mobility': 'Traslado',
  'home.service.flight.label': 'Aéreo',
  'home.service.hotel.label': 'Hotel',
  'home.service.bus.label': 'Autobús',
  'home.service.rental.label': 'Autos',
  'home.service.mobility.label': 'Movilidad',
  ...voucherStatusEntries('es'),
  'trips.cabin.economy': 'Económica',
  'trips.cabin.premium': 'Premium',
  'trips.cabin.business': 'Ejecutiva',
  'trips.cabin.first': 'Primera',
  'trips.panel.itinerary': 'Itinerario',
  'trips.panel.total': 'Total del viaje',
  'doc.title': 'Documento de viaje',
  'doc.eyebrow': 'Viaje',
  'doc.generatedAt': 'Generado el {{ts}}',
  'doc.values': 'Importes',
  'doc.disclaimer':
    'Horarios en el huso local de cada ciudad. Puerta, terminal y cambios de última hora: consulta la app — este documento refleja el viaje al {{ts}}.',
  'doc.readinessIssued': 'Emitida',
  'doc.readinessPending': 'En emisión',
  'doc.tripFallback': 'Tu viaje',
  'doc.total': 'Total',
  'doc.moreInfo': 'Más información',
} satisfies Record<string, string>;

// Sem anotação de tipo explícita: `Record<string, string>` apagaria as chaves
// literais de PT_BR/EN/ES. Preservar a chave literal por locale é o que o
// spread `{ ...CATALOG['pt-BR'], ...ptBrCommon }` do web-chat precisa para não
// colapsar em `string` — mesmo hoje sem uma `declare module 'i18next'` que
// ligue `ParseKeys` a esse objeto, é a tipagem correta para o consumidor.
export const CATALOG = {
  'pt-BR': {
    ...PT_BR,
    ...FLIGHT_VOUCHER_CATALOG['pt-BR'],
    ...HOTEL_VOUCHER_CATALOG['pt-BR'],
    ...BUS_VOUCHER_CATALOG['pt-BR'],
    ...RENTAL_VOUCHER_CATALOG['pt-BR'],
    ...PAYMENT_VOUCHER_CATALOG['pt-BR'],
    ...MOBILITY_VOUCHER_CATALOG['pt-BR'],
    ...EXPENSE_VOUCHER_CATALOG['pt-BR'],
    ...SCHEDULED_RIDE_VOUCHER_CATALOG['pt-BR'],
    ...REQUEST_SCREENS_CATALOG['pt-BR'],
  },
  en: {
    ...EN,
    ...FLIGHT_VOUCHER_CATALOG.en,
    ...HOTEL_VOUCHER_CATALOG.en,
    ...BUS_VOUCHER_CATALOG.en,
    ...RENTAL_VOUCHER_CATALOG.en,
    ...PAYMENT_VOUCHER_CATALOG.en,
    ...MOBILITY_VOUCHER_CATALOG.en,
    ...EXPENSE_VOUCHER_CATALOG.en,
    ...SCHEDULED_RIDE_VOUCHER_CATALOG.en,
    ...REQUEST_SCREENS_CATALOG.en,
  },
  es: {
    ...ES,
    ...FLIGHT_VOUCHER_CATALOG.es,
    ...HOTEL_VOUCHER_CATALOG.es,
    ...BUS_VOUCHER_CATALOG.es,
    ...RENTAL_VOUCHER_CATALOG.es,
    ...PAYMENT_VOUCHER_CATALOG.es,
    ...MOBILITY_VOUCHER_CATALOG.es,
    ...EXPENSE_VOUCHER_CATALOG.es,
    ...SCHEDULED_RIDE_VOUCHER_CATALOG.es,
    ...REQUEST_SCREENS_CATALOG.es,
  },
};
